import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import useSEO, { SEO_CONFIG } from '@/hooks/useSEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Home() {
  const [beers, setBeers] = useState([]);
  
  // SEO optimization for homepage
  useSEO(SEO_CONFIG.home);

  useEffect(() => {
    const fetchBeers = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        // Get only individual beers (not bundles)
        const individualBeers = response.data.filter(p => !p.is_bundle);
        setBeers(individualBeers);
      } catch (error) {
        // erreur de chargement silencieuse
      }
    };
    fetchBeers();
  }, []);

  return (
    <div>
      <section className="alpilles-topo-hero relative h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="relative z-10 text-center max-w-5xl mx-auto px-4">
          <h1 className="text-6xl sm:text-7xl lg:text-8xl font-heading text-white mb-8 leading-tight tracking-tight drop-shadow-2xl">
            Brasserie artisanale à Saint-Rémy-de-Provence
          </h1>
          <p className="text-2xl sm:text-3xl text-white/90 mb-12 font-medium drop-shadow-lg">
            Soif de Provence est une brasserie artisanale à Saint-Rémy-de-Provence : des bières artisanales de caractère, brassées avec passion au cœur des Alpilles.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/nos-bieres">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-10 py-7 text-lg font-semibold transition-all hover:shadow-2xl" data-testid="hero-shop-button">
                Découvrir nos bières artisanales
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/points-de-vente">
              <Button variant="outline" className="border-2 border-white/40 text-white hover:bg-white/10 hover:border-white/60 rounded-full px-10 py-7 text-lg font-semibold transition-all backdrop-blur-sm" data-testid="hero-points-de-vente-button">
                Trouver un point de vente
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-heading text-foreground mb-6">Notre savoir-faire artisanal</h2>
            <p className="text-lg text-foreground/70 max-w-2xl mx-auto">
              Nos bières artisanales sont brassées avec passion à Saint-Rémy-de-Provence, au cœur des Alpilles, à partir d’ingrédients sélectionnés avec soin.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            <div className="bg-secondary p-8 rounded-2xl border border-border hover:shadow-xl transition-shadow" data-testid="feature-artisanal">
              <h3 className="text-2xl font-heading text-foreground mb-4">Savoir-faire artisanal</h3>
              <p className="text-foreground/70">
                Chaque bière est brassée avec rigueur dans notre brasserie de Saint-Rémy, selon des méthodes éprouvées.
              </p>
            </div>
            
            <div className="bg-secondary p-8 rounded-2xl border border-border hover:shadow-xl transition-shadow" data-testid="feature-local">
              <h3 className="text-2xl font-heading text-foreground mb-4">Ingrédients sélectionnés</h3>
              <p className="text-foreground/70">
                Nous choisissons nos matières premières avec soin pour garantir cohérence et qualité constante.
              </p>
            </div>
            
            <div className="bg-secondary p-8 rounded-2xl border border-border hover:shadow-xl transition-shadow" data-testid="feature-passion">
              <h3 className="text-2xl font-heading text-foreground mb-4">Contrôle qualité</h3>
              <p className="text-foreground/70">
                À chaque étape du brassage, nous appliquons des standards stricts pour vous offrir des bières d’exception.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-heading text-foreground mb-6">Nos bières</h2>
            <p className="text-lg text-foreground/70">
              Une gamme variée, de la légère Blanche au puissant Stout.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {beers.map((beer) => (
              <div
                key={beer.id}
                className="group relative bg-white rounded-2xl overflow-hidden border border-stone-200 hover:border-primary/30 transition-all duration-300 hover:shadow-xl"
                data-testid={`beer-card-${beer.name.toLowerCase()}`}
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={beer.image_url}
                    alt={beer.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-3xl font-heading text-white mb-1 drop-shadow-lg">{beer.name}</h3>
                    <p className="text-sm text-white/90 drop-shadow-md">{beer.style}</p>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-foreground/70 mb-3">{beer.short_description}</p>
                  {beer.abv && (
                    <div className="flex items-center space-x-4 text-sm text-foreground/60">
                      <span>ABV: {beer.abv}</span>
                      {beer.ibu && <span>IBU: {beer.ibu}</span>}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/nos-bieres">
              <Button className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 py-6 shadow-md hover:shadow-lg transition-all" data-testid="view-all-beers-button">
                Découvrir toute la gamme
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://customer-assets.emergentagent.com/job_craft-beer-shop-1/artifacts/os8dn1xx_6361d69d-2a73-4d83-b69c-68cf83094b94~1.jpg"
                alt="Notre brasserie"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="text-4xl lg:text-5xl font-heading text-foreground mb-6">
                Brassée avec exigence
              </h2>
              <p className="text-lg text-foreground/80 mb-6">
                Chaque bière que nous produisons est le fruit d&apos;un savoir-faire rigoureux et d&apos;une attention constante à la qualité. De la sélection des matières premières au contrôle de chaque étape de fermentation, nous appliquons les mêmes standards d&apos;excellence.
              </p>
              <p className="text-lg text-foreground/80 mb-6">
                Notre approche repose sur la cohérence des ingrédients, la maîtrise du processus de brassage, et une recherche permanente de l&apos;équilibre parfait entre tradition et innovation.
              </p>
              <p className="text-lg text-foreground/80">
                Le résultat : des bières de caractère qui reflètent notre engagement envers la qualité et l&apos;authenticité.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="alpilles-topo-bg py-24 lg:py-32">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl lg:text-5xl font-heading text-white mb-6">
                Location de tireuse
              </h2>
              <p className="text-lg text-white/80 mb-8">
                Pour vos événements (mariages, anniversaires, fêtes), louez notre tireuse avec nos fûts de bière artisanale.
              </p>
              <ul className="space-y-3 mb-8 text-white/80">
                <li className="flex items-start">
                  <span className="text-accent mr-2">✓</span>
                  <span>Tireuse mise à disposition (selon volume commandé)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-accent mr-2">✓</span>
                  <span>Fûts de 30 litres de bière artisanale</span>
                </li>
                <li className="flex items-start">
                  <span className="text-accent mr-2">✓</span>
                  <span>Livraison et installation possibles</span>
                </li>
              </ul>
              <Link to="/location-tireuse">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-medium transition-all hover:shadow-xl" data-testid="tap-rental-cta-button">
                  Demander un devis
                </Button>
              </Link>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-white/10">
              <img
                src="https://customer-assets.emergentagent.com/job_craft-beer-shop-1/artifacts/x8yagodb_PXL_20250305_104046804.jpg"
                alt="Tireuse à bière professionnelle Soif de Provence"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block text-accent font-semibold tracking-wide uppercase text-sm mb-4">Professionnels</span>
          <h2 className="text-4xl lg:text-5xl font-heading text-foreground mb-6">
            Une bière artisanale locale pour votre établissement
          </h2>
          <p className="text-lg text-foreground/70 mb-10 max-w-2xl mx-auto">
            Restaurants, bars, campings, cavistes, épiceries fines, associations et comités des fêtes :
            proposez à vos clients une bière médaillée, brassée à Saint-Rémy-de-Provence. Livraison les mardis et jeudis.
          </p>
          <Link to="/professionnels">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-medium transition-all hover:shadow-xl" data-testid="home-pro-cta-button">
              Découvrir notre offre professionnels
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}